import { LucideIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useRef, useEffect, useState } from 'react';

interface NavigationButtonProps {
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  collapsed?: boolean;
  variant?: 'default' | 'section-header';
  indent?: boolean;
}

export function NavigationButton({
  icon: Icon,
  label,
  isActive = false,
  onClick,
  collapsed = false,
  variant = 'default',
  indent = false,
}: NavigationButtonProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const textRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Check if text is overflowing and needs ticker animation
  useEffect(() => {
    if (collapsed || !textRef.current || !containerRef.current) {
      setIsOverflowing(false);
      return;
    }

    const checkOverflow = () => {
      if (textRef.current && containerRef.current) {
        const textWidth = textRef.current.scrollWidth;
        const containerWidth = containerRef.current.clientWidth;
        setIsOverflowing(textWidth > containerWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [label, collapsed]);

  return (
    <motion.div
      onClick={onClick}
      className={`
        relative overflow-hidden backdrop-blur-xl cursor-pointer
        box-border content-stretch flex flex-row items-center rounded-xl
        ${collapsed ? 'gap-0 justify-center p-2.5' : 'gap-3.5 px-3 py-2.5'}
        ${indent && !collapsed ? 'ml-6' : ''}
        ${variant === 'section-header' ? 'font-semibold' : ''}
      `}
      initial={false}
      animate={{
        backgroundColor: isActive
          ? (isDark ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.25)')
          : 'transparent',
        borderColor: isActive
          ? (isDark ? 'rgba(75, 85, 99, 0.4)' : 'rgba(255, 255, 255, 0.3)')
          : 'transparent',
        boxShadow: isActive
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
          : '0 0 0 0 rgba(0, 0, 0, 0)',
      }}
      style={{ borderWidth: '1px', borderStyle: 'solid' }}
      whileHover={{
        scale: 1.015,
        x: collapsed ? 0 : 2,
        backgroundColor: isActive
          ? (isDark ? 'rgba(55, 65, 81, 0.6)' : 'rgba(255, 255, 255, 0.3)')
          : (isDark ? 'rgba(55, 65, 81, 0.35)' : 'rgba(255, 255, 255, 0.12)'),
        borderColor: isActive
          ? (isDark ? 'rgba(75, 85, 99, 0.5)' : 'rgba(255, 255, 255, 0.35)')
          : (isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(255, 255, 255, 0.15)')
      }}
      whileTap={{ scale: 0.985 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 28,
        backgroundColor: { duration: 0.15, ease: 'easeOut' },
        borderColor: { duration: 0.15, ease: 'easeOut' },
        boxShadow: { duration: 0.15, ease: 'easeOut' }
      }}
    >
      {/* Subtle gradient overlay on active */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent pointer-events-none"
        style={{ opacity: isActive ? 1 : 0, transition: 'opacity 0.2s ease-out' }}
      />

      <Icon
        className={`
          relative shrink-0 z-10 transition-colors duration-200
          ${isActive ? 'text-black dark:text-white' : 'text-gray-600 dark:text-gray-400'}
        `}
        size={24}
      />

      <AnimatePresence mode="wait">
        {!collapsed && (
          <motion.div
            ref={containerRef}
            className={`
              font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]
              font-[590] leading-[0] relative flex-1 text-[15px] z-10 max-w-full
              ${isOverflowing ? 'ticker-container' : ''}
              ${isActive ? 'text-black dark:text-white' : 'text-gray-600 dark:text-gray-400'}
            `}
            style={{ transition: 'color 0.2s ease-out' }}
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p 
              ref={textRef}
              className={`
                block leading-[normal] whitespace-nowrap
                ${isOverflowing ? 'ticker-text' : ''}
              `}
            >
              {label}
              {isOverflowing && ` • ${label}`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

